import { useState, useEffect } from 'react';
import { User, UserProgram } from '../../types/user.response';
import { CatalogProgram, CatalogCollege } from '../../types/catalog.response';
import { usersApi } from '../../services/api/users.api';
import SearchableDropdown from '../../components/shared/SearchableDropdown';

interface ProgramSettingsProps {
    user: User;
    programs: CatalogProgram[];
    colleges: CatalogCollege[];
    catalogLoading: boolean;
    refreshAuth: () => Promise<void>;
}

export default function ProgramSettings({
    user,
    programs,
    colleges,
    catalogLoading,
    refreshAuth,
}: ProgramSettingsProps) {
    const [selectedCollege, setSelectedCollege] = useState<CatalogCollege | null>(null);
    const [availableMajors, setAvailableMajors] = useState<CatalogProgram[]>([]);
    const [allMinors, setAllMinors] = useState<CatalogProgram[]>([]);
    const [selectedMajors, setSelectedMajors] = useState<UserProgram[]>([]);
    const [selectedMinors, setSelectedMinors] = useState<UserProgram[]>([]);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Initialize from user data once catalog is loaded
    useEffect(() => {
        if (catalogLoading || colleges.length === 0 || programs.length === 0) return;

        const college = colleges.find(c => c.collegeId === user.college.collegeId) ?? null;
        setSelectedCollege(college);

        const userMajors = user.programs.filter(p => p.type === 'major');
        const userMinors = user.programs.filter(p => p.type === 'minor');
        setSelectedMajors(userMajors.length > 0 ? userMajors : [{ programId: '', name: '', type: 'major', concentrationNames: [] }]);
        setSelectedMinors(userMinors);
    }, [user, colleges, programs, catalogLoading]);

    // Filter majors/minors from catalog
    useEffect(() => {
        const majors = programs
            .filter(p => p.type === 'major')
            .sort((a, b) => a.name.localeCompare(b.name));
        const minors = programs
            .filter(p => p.type === 'minor')
            .sort((a, b) => a.name.localeCompare(b.name));

        setAllMinors(minors);
        setAvailableMajors(
            majors.filter(m => m.colleges.some(c => c.collegeId === selectedCollege?.collegeId))
        );
    }, [programs, selectedCollege]);

    const handleCollegeSelect = (collegeId: string) => {
        const college = colleges.find(c => c.collegeId === collegeId);
        if (!college) return;
        setSelectedCollege(college);
        setSelectedMajors([{ programId: '', name: '', type: 'major', concentrationNames: [] }]);
        setError(null);
    };

    const handleMajorSelect = (index: number, majorId: string) => {
        const major = availableMajors.find(m => m.programId === majorId);
        if (!major) return;
        setSelectedMajors(prev => {
            const next = [...prev];
            next[index] = { programId: major.programId, name: major.name, type: 'major', concentrationNames: [] };
            return next;
        });
        setError(null);
    };

    const handleMajorConcentrationSelect = (index: number, name: string) => {
        setSelectedMajors(prev => {
            const next = [...prev];
            next[index] = { ...next[index], concentrationNames: [name] };
            return next;
        });
        setError(null);
    };

    const handleMinorSelect = (index: number, minorId: string) => {
        const minor = allMinors.find(m => m.programId === minorId);
        if (!minor) return;
        setSelectedMinors(prev => {
            const next = [...prev];
            next[index] = { programId: minor.programId, name: minor.name, type: 'minor', concentrationNames: [] };
            return next;
        });
        setError(null);
    };

    const handleMinorConcentrationSelect = (index: number, name: string) => {
        setSelectedMinors(prev => {
            const next = [...prev];
            next[index] = { ...next[index], concentrationNames: [name] };
            return next;
        });
        setError(null);
    };

    const addMajorDropdown = () => {
        setSelectedMajors(prev => [...prev, { programId: '', name: '', type: 'major', concentrationNames: [] }]);
    };

    const removeMajorDropdown = (index: number) => {
        setSelectedMajors(prev => prev.filter((_, i) => i !== index));
    };

    const addMinorDropdown = () => {
        setSelectedMinors(prev => [...prev, { programId: '', name: '', type: 'minor', concentrationNames: [] }]);
    };

    const removeMinorDropdown = (index: number) => {
        setSelectedMinors(prev => prev.filter((_, i) => i !== index));
    };

    const validate = (): string | null => {
        if (!selectedCollege) return 'Please select a college';
        if (selectedMajors.some(m => !m.programId)) return 'Please select all majors';

        for (const major of selectedMajors) {
            const catalogMajor = availableMajors.find(m => m.programId === major.programId);
            if (catalogMajor?.concentrations?.length && major.concentrationNames.length === 0) {
                return `Please select a concentration for ${catalogMajor.name}`;
            }
        }

        if (selectedMinors.some(m => !m.programId)) return 'Please select all minors or remove empty minor selections';

        for (const minor of selectedMinors) {
            const catalogMinor = allMinors.find(m => m.programId === minor.programId);
            if (catalogMinor?.concentrations?.length && minor.concentrationNames.length === 0) {
                return `Please select a concentration for ${catalogMinor.name}`;
            }
        }

        return null;
    };

    const handleSave = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setSaving(true);
            setError(null);
            const collegePrograms = user.programs.filter(p => p.type === 'college');
            await usersApi.updateProfile({
                collegeId: selectedCollege!.collegeId,
                programs: [...collegePrograms, ...selectedMajors, ...selectedMinors],
            });
            await refreshAuth();
            setSuccess(true);
            setTimeout(() => setSuccess(false), 2000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update programs');
        } finally {
            setSaving(false);
        }
    };

    if (catalogLoading) {
        return <p className="text-sm text-gray-500">Loading program data...</p>;
    }

    return (
        <div className="space-y-10">
            {/* Error display */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    <p className="text-sm">{error}</p>
                </div>
            )}

            {/* College Section */}
            <section>
                <h2 className="text-base font-medium text-gray-900 mb-1">College</h2>
                <p className="text-sm text-gray-500 mb-4">Your college at Cornell.</p>
                <div className="max-w-md">
                    <SearchableDropdown
                        value={selectedCollege?.collegeId || ''}
                        options={colleges.map(c => ({ _id: c.collegeId, name: c.name }))}
                        onChange={handleCollegeSelect}
                        excludeIds={[]}
                        placeholder="Search for a college..."
                        noResultsText="No colleges found"
                    />
                </div>
            </section>

            <hr className="border-gray-200" />

            {/* Majors Section */}
            <section>
                <h2 className="text-base font-medium text-gray-900 mb-1">Major</h2>
                <p className="text-sm text-gray-500 mb-4">Select your major(s) and any required concentrations.</p>

                <div className="space-y-4 mb-4">
                    {selectedMajors.map((selectedMajor, index) => {
                        const majorData = availableMajors.find(m => m.programId === selectedMajor.programId);
                        const hasConcentrations = majorData?.concentrations && majorData.concentrations.length > 0;

                        return (
                            <div key={index} className="py-2">
                                <div className="flex items-center gap-2 max-w-md">
                                    <SearchableDropdown
                                        value={selectedMajor.programId}
                                        options={availableMajors.map(m => ({ _id: m.programId, name: m.name }))}
                                        onChange={(id) => handleMajorSelect(index, id)}
                                        excludeIds={selectedMajors.filter((_, i) => i !== index).map(m => m.programId).filter(Boolean)}
                                        placeholder="Search for a major..."
                                        noResultsText="No majors found"
                                    />
                                    {index > 0 && (
                                        <button type="button" onClick={() => removeMajorDropdown(index)} className="text-gray-400 hover:text-gray-600">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    )}
                                </div>

                                {hasConcentrations && selectedMajor.programId && (
                                    <div className="mt-3 pl-8 max-w-md">
                                        <SearchableDropdown
                                            value={selectedMajor.concentrationNames[0] || ''}
                                            options={(majorData.concentrations || []).map(name => ({ _id: name, name }))}
                                            onChange={(c) => handleMajorConcentrationSelect(index, c)}
                                            excludeIds={[]}
                                            placeholder="Select a concentration..."
                                            noResultsText="No concentrations found"
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <button
                    type="button"
                    onClick={addMajorDropdown}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add another major
                </button>
            </section>

            <hr className="border-gray-200" />

            {/* Minors Section */}
            <section>
                <h2 className="text-base font-medium text-gray-900 mb-1">Minor</h2>
                <p className="text-sm text-gray-500 mb-4">Select your minor(s) and any required concentrations.</p>

                <div className="space-y-4 mb-4">
                    {selectedMinors.map((selectedMinor, index) => {
                        const minorData = allMinors.find(m => m.programId === selectedMinor.programId);
                        const hasConcentrations = minorData?.concentrations && minorData.concentrations.length > 0;

                        return (
                            <div key={index} className="py-2">
                                <div className="flex items-center gap-2 max-w-md">
                                    <SearchableDropdown
                                        value={selectedMinor.programId}
                                        options={allMinors.map(m => ({ _id: m.programId, name: m.name }))}
                                        onChange={(id) => handleMinorSelect(index, id)}
                                        excludeIds={selectedMinors.filter((_, i) => i !== index).map(m => m.programId).filter(Boolean)}
                                        placeholder="Search for a minor..."
                                        noResultsText="No minors found"
                                    />
                                    <button type="button" onClick={() => removeMinorDropdown(index)} className="text-gray-400 hover:text-gray-600">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>

                                {hasConcentrations && selectedMinor.programId && (
                                    <div className="mt-3 pl-8 max-w-md">
                                        <SearchableDropdown
                                            value={selectedMinor.concentrationNames[0] || ''}
                                            options={(minorData.concentrations || []).map(name => ({ _id: name, name }))}
                                            onChange={(c) => handleMinorConcentrationSelect(index, c)}
                                            excludeIds={[]}
                                            placeholder="Select a concentration..."
                                            noResultsText="No concentrations found"
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <button
                    type="button"
                    onClick={addMinorDropdown}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add a minor
                </button>
            </section>

            {/* Request program link */}
            <p className="text-sm text-gray-500">
                Don't see your program?{' '}
                <a
                    href="https://forms.gle/yGzNkoLcPaq8JZih9"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline hover:text-blue-700"
                >
                    Request it here
                </a>
            </p>

            {/* Save button */}
            <div className="flex items-center gap-3 pt-2">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
                {success && <span className="text-sm text-green-600">Saved successfully</span>}
            </div>
        </div>
    );
}
